import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { formatDate, titleCase, toDate } from '../../lib/format'
import type { ListResponse } from '../../types'
import type { GovLicence } from './types'
import { LICENCE_STATUSES } from './types'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { ShieldIcon } from '../../components/icons'

// Mirrors the backend api_gov_licence_status gate - gov_inspector (the most
// junior gov tier) can view licences but never change their status.
const DECISION_ROLES = new Set(['super_admin', 'gov_director', 'gov_regional'])

const FILTERS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
]

/** Flags licences expiring within 30 days so renewals aren't missed. */
function expiryTone(expiry?: string): { tone: 'danger' | 'warning' | null; label: string } | null {
  const d = toDate(expiry)
  if (!d) return null
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000)
  if (days < 0) return { tone: 'danger', label: `expired ${Math.abs(days)}d ago` }
  if (days <= 30) return { tone: 'warning', label: `${days}d left` }
  return null
}

export default function GovLicences() {
  const { user } = useAuth()
  const canDecide = DECISION_ROLES.has(user?.role ?? '')
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'licences', status],
    queryFn: () => api.get<ListResponse<GovLicence>>(
      `/gov/licences${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  })

  const setStatusMut = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) =>
      api.post(`/gov/licences/${id}/status`, { status: next }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gov'] }); setOpenId(null) },
  })

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Licences</h1>
          <p className="page-head__sub">Mining licences and their current standing.</p>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 'var(--sp-5)', flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <Button key={f.label} size="sm"
                  variant={status === f.value ? 'primary' : 'secondary'}
                  onClick={() => setStatus(f.value)}>
            {f.label}
          </Button>
        ))}
      </div>

      {setStatusMut.isError && <Alert tone="danger">{(setStatusMut.error as Error).message}</Alert>}

      <Card>
        <CardHeader title="Licences"
                    sub={items.length ? `${items.length} licence${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="72%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<ShieldIcon size={22} />} title="No licences"
                        body="Licences matching this filter don't exist yet." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Licence</th><th>Cooperative</th><th>Mineral</th>
                      <th>Expires</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {items.map((l, i) => {
                    const id = l.id ?? String(i)
                    const exp = expiryTone(l.expiry_date)
                    return (
                      <Fragment key={id}>
                        <tr>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                            {l.license_number || l.id || '—'}
                          </td>
                          <td>{l.org_id || '—'}</td>
                          <td>{titleCase(l.mineral_type)}</td>
                          <td>
                            <div className="row" style={{ gap: 'var(--sp-2)' }}>
                              {formatDate(l.expiry_date)}
                              {exp && <Badge tone={exp.tone!}>{exp.label}</Badge>}
                            </div>
                          </td>
                          <td><StatusBadge status={l.status} /></td>
                          <td className="table__num">
                            {l.id && canDecide && (
                              <Button size="sm" variant="secondary"
                                      onClick={() => setOpenId(openId === id ? null : id)}>
                                {openId === id ? 'Close' : 'Change status'}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {openId === id && l.id && canDecide && (
                          <tr>
                            <td colSpan={6} style={{ background: 'var(--bg-sunken)' }}>
                              <div className="stack" style={{ padding: 'var(--sp-2) 0' }}>
                                <Alert tone="warning">
                                  Suspending or cancelling a licence takes effect immediately
                                  and blocks the cooperative&rsquo;s operations.
                                </Alert>
                                <div className="row" style={{ flexWrap: 'wrap' }}>
                                  {LICENCE_STATUSES.map((s) => (
                                    <Button key={s} size="sm"
                                            variant={s === 'active' ? 'primary'
                                                   : s === 'cancelled' ? 'danger' : 'secondary'}
                                            disabled={setStatusMut.isPending || s === l.status}
                                            onClick={() => setStatusMut.mutate({ id: l.id!, next: s })}>
                                      {titleCase(s)}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
